import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Add from '@mui/icons-material/Add';
import People from '@mui/icons-material/People';
import Business from '@mui/icons-material/Business';
import Settings from '@mui/icons-material/Settings';
import MonitorHeart from '@mui/icons-material/MonitorHeart';
import Build from '@mui/icons-material/Build';
import DeleteSweep from '@mui/icons-material/DeleteSweep';
import {
  tenantConfigurationService,
  TenantSummaryDto,
  CreateTenantRequest,
} from '../services/tenantConfigurationService';
import { PlatformSettingsPanel } from '../components/admin/PlatformSettingsPanel';
import { SystemHealthPanel } from '../components/admin/SystemHealthPanel';
import { DatabaseMaintenancePanel } from '../components/admin/DatabaseMaintenancePanel';
import { DemoCleanupPanel } from '../components/admin/DemoCleanupPanel';

const defaultNewTenant = (): CreateTenantRequest => ({
  tenantId: '',
  displayName: '',
  seatLimit: 50,
  dailyManualUploadLimit: 200,
  dailyBatchLimit: 2000,
  maxAudioDurationMinutes: 60,
  timeZoneId: 'America/New_York',
  contractStartDate: new Date().toISOString().split('T')[0],
  contractEndDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
});

export const PlatformAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<TenantSummaryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newTenant, setNewTenant] = useState<CreateTenantRequest>(defaultNewTenant);

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const data = await tenantConfigurationService.getAllTenants();
      setTenants(data);
      setError(null);
    } catch {
      setError('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async () => {
    try {
      setCreating(true);
      setCreateError(null);
      await tenantConfigurationService.createTenant(newTenant);
      setDialogOpen(false);
      setNewTenant(defaultNewTenant());
      await loadTenants();
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string }).message
        : 'Failed to create tenant';
      setCreateError(message);
    } finally {
      setCreating(false);
    }
  };

  const handleTenantClick = (tenantId: string) => {
    // Navigate to settings page with tenant context
    navigate(`/settings?tenant=${tenantId}`);
  };

  const activeTenants = tenants.filter(t => t.isActive);
  const inactiveTenants = tenants.filter(t => !t.isActive);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h4" component="h1">
          Platform Administration
        </Typography>
      </Box>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<Business />} iconPosition="start" label="Tenants" />
        <Tab icon={<Settings />} iconPosition="start" label="Platform Settings" />
        <Tab icon={<MonitorHeart />} iconPosition="start" label="System Health" />
        <Tab icon={<Build />} iconPosition="start" label="DB Maintenance" />
        <Tab icon={<DeleteSweep />} iconPosition="start" label="Demo Cleanup" />
      </Tabs>

      {activeTab === 1 && <PlatformSettingsPanel />}

      {activeTab === 2 && <SystemHealthPanel onNavigateToSettings={() => setActiveTab(1)} />}

      {activeTab === 3 && <DatabaseMaintenancePanel />}

      {activeTab === 4 && <DemoCleanupPanel />}

      {activeTab === 0 && (
      <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setDialogOpen(true)}
        >
          Add New Tenant
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Active Tenants */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Active Tenants ({activeTenants.length})
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {activeTenants.map(tenant => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={tenant.tenantId}>
            <Card elevation={2}>
              <CardActionArea onClick={() => handleTenantClick(tenant.tenantId)}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Business sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" noWrap>
                      {tenant.displayName}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    ID: {tenant.tenantId}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      icon={<People />}
                      label={`${tenant.userCount} users`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`${tenant.seatLimit} seats`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Contract: {new Date(tenant.contractStartDate).toLocaleDateString()} – {new Date(tenant.contractEndDate).toLocaleDateString()}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
        {activeTenants.length === 0 && (
          <Grid size={{ xs: 12 }}>
            <Alert severity="info">No active tenants. Click "Add New Tenant" to get started.</Alert>
          </Grid>
        )}
      </Grid>

      {/* Inactive Tenants */}
      {inactiveTenants.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 2 }} color="text.secondary">
            Inactive Tenants ({inactiveTenants.length})
          </Typography>
          <Grid container spacing={2}>
            {inactiveTenants.map(tenant => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={tenant.tenantId}>
                <Card elevation={1} sx={{ opacity: 0.7 }}>
                  <CardActionArea onClick={() => handleTenantClick(tenant.tenantId)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Business sx={{ mr: 1, color: 'text.disabled' }} />
                        <Typography variant="h6" noWrap color="text.secondary">
                          {tenant.displayName}
                        </Typography>
                      </Box>
                      <Chip label="Inactive" size="small" color="default" />
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}
      </>
      )}

      {/* Create Tenant Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Tenant</DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
          <TextField
            label="Tenant ID"
            helperText="Lowercase, no spaces (e.g., 'acme-pd')"
            fullWidth
            margin="normal"
            value={newTenant.tenantId}
            onChange={e => setNewTenant({ ...newTenant, tenantId: e.target.value.toLowerCase().replace(/\s/g, '') })}
          />
          <TextField
            label="Display Name"
            helperText="Human-readable name (e.g., 'Acme Police Department')"
            fullWidth
            margin="normal"
            value={newTenant.displayName}
            onChange={e => setNewTenant({ ...newTenant, displayName: e.target.value })}
          />
          <TextField
            label="Seat Limit"
            type="number"
            fullWidth
            margin="normal"
            value={newTenant.seatLimit}
            onChange={e => setNewTenant({ ...newTenant, seatLimit: parseInt(e.target.value) || 0 })}
          />
          <TextField
            label="Contract Start Date"
            type="date"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={newTenant.contractStartDate}
            onChange={e => setNewTenant({ ...newTenant, contractStartDate: e.target.value })}
          />
          <TextField
            label="Contract End Date"
            type="date"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={newTenant.contractEndDate}
            onChange={e => setNewTenant({ ...newTenant, contractEndDate: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateTenant}
            disabled={creating || !newTenant.tenantId || !newTenant.displayName}
          >
            {creating ? <CircularProgress size={20} /> : 'Create Tenant'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
